import { fieldBelongsToRecipient } from './fieldAssignments';
import { LightSigningSurface } from './LightSigningSurface';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/joy';
import {
  FileText,
  User,
  CheckCircle,
  XCircle as AlertCircle,
  PaperPlaneTilt as Send
} from '@phosphor-icons/react';
import '../../../lib/pdfWorkerSetup'; // Initialize PDF worker
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import SigningFieldRenderer from './SigningFieldRenderer';
import { CompletedFieldRenderer } from './CompletedFieldRenderer';
import { SignatureModal } from './SignatureModal';
import type { ContractField as BaseContractField, ContractRecipient, ContractField } from '../../../types';

// API Contract Field type
interface ApiContractField {
  id: string | number;  // Can be either UUID string or number
  field_type: string;
  type?: string;
  name?: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  signer: string;
  label?: string;
  options?: string | string[];
  value?: any;
  placeholder?: string;
}

interface Contract {
  id: number;
  title: string;
  contract_number: string;
  pdf_url?: string;
  contract_fields: ApiContractField[];
  status?: string;
}

interface SimplifiedSigningInterfaceProps {
  signingToken: string;
  contractId: number;
}

const hasFieldValue = (value: any) => value !== undefined && value !== '' && value !== null;

const SimplifiedSigningInterfaceContent: React.FC<SimplifiedSigningInterfaceProps> = ({
  signingToken,
  contractId
}) => {
  const [contract, setContract] = useState<Contract | null>(null);
  const [recipient, setRecipient] = useState<ContractRecipient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.0);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [activeSignatureField, setActiveSignatureField] = useState<string | null>(null);
  // Values already recorded on the server when the session opened - those belong to
  // a step that is already done and must not be overwritten from here
  const recordedValuesRef = useRef<Record<string, any>>({});

  // Load signing session data
  useEffect(() => {
    const loadSigningData = async () => {
      try {
        const response = await fetch(`/api/crm/contracts/signing/${signingToken}/`);
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to load contract');
        }

        const data = await response.json();
        console.log('Loaded signing session data:', {
          contractId: data.contract?.id,
          recipientRole: data.recipient?.role,
          recipientEmail: data.recipient?.email,
          recipientName: data.recipient?.name,
          totalFields: data.contract?.contract_fields?.length,
          fields: data.contract?.contract_fields?.map((f: any) => ({
            id: f.id,
            type: f.type || f.field_type,
            signer: f.signer,
            required: f.required
          }))
        });
        setContract(data.contract);
        setRecipient(data.recipient);

        // Initialize field values with any existing data from field_values object
        const initialValues: Record<string, any> = data.field_values || {};
        recordedValuesRef.current = initialValues;
        setFieldValues(initialValues);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load contract');
      } finally {
        setLoading(false);
      }
    };

    loadSigningData();
  }, [contractId, signingToken]);

  // Adjust scale to fit 80% of viewport width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Calculate scale to make PDF use 80% of viewport width
      // Standard PDF width is 612px (8.5 inches at 72 DPI)
      const targetWidth = width * 0.8;
      const baseScale = targetWidth / 612;

      // Apply reasonable min/max limits
      const scale = Math.min(Math.max(baseScale, 0.5), 2.0);
      setScale(scale);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get all fields (to show other signers' completed fields as read-only)
  const getVisibleFields = useCallback(() => {
    if (!contract || !recipient) return [];
    // Return ALL fields so we can show completed fields from other signers
    return contract.contract_fields;
  }, [contract, recipient]);

  // Check if a field is editable by the current recipient. Anything entered during
  // this session stays editable so a typo or a bad signature can be corrected.
  const isFieldEditable = useCallback((field: ApiContractField) => {
    if (!recipient) return false;

    if (hasFieldValue(recordedValuesRef.current[String(field.id)])) {
      return false;
    }

    return fieldBelongsToRecipient(field, recipient);
  }, [recipient]);

  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }, []);

  const handleSignatureFieldClick = useCallback((fieldId: string) => {
    setActiveSignatureField(fieldId);
    setSignatureModalOpen(true);
  }, []);

  const handleSignatureSave = useCallback((signature: string) => {
    if (activeSignatureField) {
      handleFieldChange(activeSignatureField, signature);
    }
    setSignatureModalOpen(false);
    setActiveSignatureField(null);
  }, [activeSignatureField, handleFieldChange]);

  const handleSubmit = async () => {
    if (!contract || !recipient) return;

    const visibleFields = getVisibleFields();

    // Validate only required fields that are editable by current signer
    const editableFields = visibleFields.filter(isFieldEditable);
    const requiredFields = editableFields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !hasFieldValue(fieldValues[String(f.id)]));

    if (missingFields.length > 0) {
      setError(`Please complete all required fields (${missingFields.length} remaining)`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Extract signature fields that belong to the current signer, preferring one
      // captured in this session over a signature already recorded for them
      const signatureFields = visibleFields.filter(f => {
        const isSignature = f.type === 'signature' || f.field_type === 'signature';
        return isSignature && fieldBelongsToRecipient(f, recipient);
      });
      const orderedSignatureFields = [
        ...signatureFields.filter(isFieldEditable),
        ...signatureFields.filter(f => !isFieldEditable(f))
      ];

      const signatureData: any = {};

      // Get the signature field value for the current signer
      for (const field of orderedSignatureFields) {
        const fieldId = String(field.id);
        if (hasFieldValue(fieldValues[fieldId])) {
          signatureData.data = fieldValues[fieldId];
          signatureData.field_id = fieldId;
          break;
        }
      }

      if (signatureFields.length > 0 && !signatureData.data) {
        setError('Please add your signature before submitting');
        return;
      }

      // Log the data being sent for debugging
      const requestPayload = {
        field_values: fieldValues,
        signature_data: signatureData,
      };
      console.log('Submitting contract signing with payload:', requestPayload);
      console.log('Signature data:', signatureData);
      console.log('Field values:', fieldValues);

      // Save field values and complete signing
      const response = await fetch(`/api/crm/contracts/signing/${signingToken}/complete/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        // Try to get the actual error message from the response
        let errorMessage = 'Failed to submit signature';
        try {
          const errorData = await response.json();
          console.error('Backend error response:', errorData);
          errorMessage = errorData.error || errorData.detail || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        throw new Error(errorMessage);
      }

      setCompleted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !contract) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert color="danger">
          <AlertCircle />
          {error}
        </Alert>
      </Box>
    );
  }

  if (completed) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 5 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <CheckCircle size={48} color="var(--joy-palette-success-500)" />
            <Typography level="h3" sx={{ mt: 2 }}>
              Contract Signed Successfully!
            </Typography>
            <Typography level="body-md" sx={{ mt: 2 }}>
              Thank you for signing the contract. You will receive a copy via email shortly.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!contract || !recipient) {
    return null;
  }

  // Get all fields
  const visibleFields = getVisibleFields();

  // Separate editable and read-only fields
  const editableFields = visibleFields.filter(isFieldEditable);
  const readOnlyFields = visibleFields.filter(f => !isFieldEditable(f));

  // Count completed required fields (only for current signer)
  const requiredFields = editableFields.filter(f => f.required);
  const completedFields = requiredFields.filter(f => hasFieldValue(fieldValues[String(f.id)]));

  // Convert API fields to the format expected by SigningFieldRenderer
  const convertedFields: BaseContractField[] = visibleFields.map(field => {
    const isEditable = isFieldEditable(field);
    // Use the value from fieldValues state (which includes all saved values) or fall back to API value
    const fieldValue = fieldValues[String(field.id)] || field.value;

    return {
      id: String(field.id),
      type: (field.type || field.field_type || 'text') as any,
      page: field.page,
      x: field.x,
      y: field.y,
      width: field.width,
      height: field.height,
      required: field.required && isEditable, // Only require if editable
      signer: field.signer,
      label: field.label,
      options: typeof field.options === 'string'
        ? field.options.split(',').map((o: string) => o.trim())
        : field.options || [],
      value: fieldValue, // Use the actual value from state or API
      // Mark as read-only if not editable for current signer
      readOnly: !isEditable
    } as BaseContractField & { readOnly?: boolean };
  });

  // Calculate progress
  const progressPercentage = requiredFields.length > 0
    ? (completedFields.length / requiredFields.length) * 100
    : 0;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.body', p: 2 }}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <FileText size={32} color="var(--joy-palette-primary-500)" />
            <Box sx={{ flex: 1 }}>
              <Typography level="h4">{contract.title}</Typography>
              <Typography level="body-sm" textColor="text.secondary">
                Contract #{contract.contract_number} • Signing as: {recipient.name}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Chip size="sm" startDecorator={<User size={12} />}>
                {recipient.role?.charAt(0).toUpperCase() + recipient.role?.slice(1)}
              </Chip>
              <Chip
                size="sm"
                color={completedFields.length === requiredFields.length ? 'success' : 'warning'}
              >
                {completedFields.length}/{requiredFields.length} Required Fields
              </Chip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Progress Bar */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="body-sm">Signing Progress</Typography>
              <Typography level="body-sm">{Math.round(progressPercentage)}%</Typography>
            </Stack>
            <LinearProgress
              determinate
              value={progressPercentage}
              color={progressPercentage === 100 ? 'success' : 'primary'}
              sx={{ height: 8 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          <AlertCircle />
          {error}
        </Alert>
      )}

      {/* Main Content - PDF with overlay fields */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {contract.pdf_url ? (
            <Document
              file={`${contract.pdf_url}?signing_token=${signingToken}`}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={(error) => {
                console.error('PDF load error:', error);
                setError('Failed to load PDF. Please refresh the page and try again.');
              }}
              loading={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              }
              error={
                <Alert color="danger" sx={{ m: 2 }}>
                  Failed to load PDF file. Please contact support if this issue persists.
                </Alert>
              }
            >
              {Array.from(new Array(numPages || 0), (el, index) => {
                // Fields use 0-indexed pages
                const pageFields = convertedFields.filter(f => f.page === index);

                // Other signers' filled fields render as read-only values; the current
                // signer's own fields stay interactive so they remain correctable
                const completedFields: ContractField[] = pageFields
                  .filter(f => (f as any).readOnly && hasFieldValue(fieldValues[f.id]))
                  .map(f => ({
                    id: f.id,
                    type: f.type,
                    page: f.page,
                    x: f.x,
                    y: f.y,
                    width: f.width,
                    height: f.height,
                    required: f.required || false,
                    signer: f.signer || '',
                    label: f.label || '',
                    options: f.options || []
                  }));

                const editableFields = pageFields.filter(f => !(f as any).readOnly);

                return (
                  <Box key={`page_${index + 1}`} sx={{ mb: 2, position: 'relative' }}>
                    <Page
                      pageNumber={index + 1}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    {/* Render completed fields with CompletedFieldRenderer for clean display */}
                    <CompletedFieldRenderer
                      fields={completedFields}
                      fieldValues={fieldValues}
                      scale={scale}
                    />
                    {/* Render editable fields with SigningFieldRenderer for interaction */}
                    <SigningFieldRenderer
                      fields={editableFields}
                      fieldValues={Object.fromEntries(
                        Object.entries(fieldValues).map(([k, v]) => [String(k), v])
                      )}
                      onFieldValueChange={handleFieldChange}
                      onSignatureFieldClick={handleSignatureFieldClick}
                      scale={scale}
                      recipientRole={recipient.role}
                      touchOptimized={true}
                    />
                  </Box>
                );
              })}
            </Document>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Alert color="warning">
                <AlertCircle />
                No PDF document available for this contract
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Submit Button */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Typography level="body-sm" textColor="text.secondary">
              Please complete all required fields and then submit your signature
            </Typography>
            <Button
              variant="solid"
              color="primary"
              size="lg"
              startDecorator={<Send />}
              onClick={handleSubmit}
              loading={submitting}
              disabled={completedFields.length !== requiredFields.length}
            >
              Submit Signature
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Signature Modal */}
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSave={handleSignatureSave}
      />
    </Box>
  );
};
export const SimplifiedSigningInterface: React.FC<SimplifiedSigningInterfaceProps> = (props) => (
  <LightSigningSurface><SimplifiedSigningInterfaceContent {...props} /></LightSigningSurface>
);
